import React, { useState, useEffect, useRef } from 'react';
import {
  Star, Plus, Edit3, Trash2, MapPin,
  Camera, Loader2, Eye, EyeOff, AlertCircle,
  Utensils, Palette, Music, Home, Package,
  IndianRupee, Users, Phone, Mail, Tag,
  Clock, CheckCircle, X, BedDouble, Search, Layers, ChevronLeft
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/axios';
import { SectionSkeleton } from '../UI/Skeleton';

import ServiceModal     from './ServiceModal';
import PhotographyModal from './photographyModal';
import DecorsModal      from './DecorsModal';
import CaterersModal    from './Caterersmodal';

// ─── Service Type Config ─────────────────────────────────────────────────────
const SERVICE_CONFIG = {
  venue: {
    label: 'Venue',
    icon: Home,
    gradient: 'from-purple-600 to-pink-600',
    bg: 'bg-purple-50',
    badge: 'bg-white/90 text-gray-800',
  },
  caterers: {
    label: 'Catering',
    icon: Utensils,
    gradient: 'from-purple-600 to-pink-600',
    bg: 'bg-purple-50',
    badge: 'bg-white/90 text-gray-800',
  },
  catering: {
    label: 'Catering',
    icon: Utensils,
    gradient: 'from-purple-600 to-pink-600',
    bg: 'bg-purple-50',
    badge: 'bg-white/90 text-gray-800',
  },
  decors: {
    label: 'Decor',
    icon: Palette,
    gradient: 'from-purple-600 to-pink-600',
    bg: 'bg-purple-50',
    badge: 'bg-white/90 text-gray-800',
  },
  decor: {
    label: 'Decor',
    icon: Palette,
    gradient: 'from-purple-600 to-pink-600',
    bg: 'bg-purple-50',
    badge: 'bg-white/90 text-gray-800',
  },
  photography: {
    label: 'Photography',
    icon: Camera,
    gradient: 'from-purple-600 to-pink-600',
    bg: 'bg-purple-50',
    badge: 'bg-white/90 text-gray-800',
  },
};

const getConfig = (type) =>
  SERVICE_CONFIG[type] || {
    label: type?.replace(/_/g, ' ') || 'Service',
    icon: Package,
    gradient: 'from-purple-600 to-pink-600',
    bg: 'bg-purple-50',
    badge: 'bg-white/90 text-gray-800',
  };

// ─── Tab Config ───────────────────────────────────────────────────────────────
const SERVICE_TABS = [
  { key: 'all',         label: 'All Services', icon: Package },
  { key: 'venue',       label: 'Venue',        icon: Home },
  { key: 'photography', label: 'Photography',  icon: Camera },
  { key: 'caterers',    label: 'Caterers',     icon: Utensils },
  { key: 'decors',      label: 'Decors',       icon: Palette },
];

// ─── Out Station Badge Helper ─────────────────────────────────────────────────
const getOutStationBadge = (val) => {
  switch (val) {
    case 'no_travel':  return { label: 'No Travel',          cls: 'bg-white text-gray-600 border-gray-200' };
    case 'client':     return { label: 'Client Pays Travel',  cls: 'bg-white text-gray-600 border-gray-200' };
    case 'included':   return { label: 'Travel Included',     cls: 'bg-white text-gray-600 border-gray-200' };
    default:           return null;
  }
};

// ─── Service Type Picker Modal ────────────────────────────────────────────────
const SERVICE_TYPES_LIST = [
  { type: 'venue',       label: 'Venue',        icon: Home,     gradient: 'from-purple-600 to-pink-600',   desc: 'Halls, resorts, farmhouses' },
  { type: 'caterers',    label: 'Catering',     icon: Utensils, gradient: 'from-purple-600 to-pink-600',   desc: 'Food & beverage services' },
  { type: 'decors',      label: 'Decor',        icon: Palette,  gradient: 'from-purple-600 to-pink-600',      desc: 'Floral, themed setups' },
  { type: 'photography', label: 'Photography',  icon: Camera,   gradient: 'from-purple-600 to-pink-600',  desc: 'Photo & video coverage' },
  // { type: 'music',       label: 'Music & Ent.', icon: Music,    gradient: 'from-purple-600 to-pink-600',    desc: 'DJs, bands, performers' },
];

const ServiceTypePicker = ({ isOpen, onSelect, onClose }) => {
  if (!isOpen) return null;
  return (
    <div className="space-y-4">
      <button
        onClick={onClose}
        className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-800 transition"
      >
        <X className="w-4 h-4" />
        Back to Services
      </button>

      <div className="bg-white rounded-2xl border border-gray-200/70 shadow-[0_1px_3px_rgba(16,24,40,0.04)] overflow-hidden">
        <div className="bg-gradient-to-br from-purple-600 to-pink-500 px-7 py-6 text-white">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-white/20 rounded-xl">
              <Plus className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Add New Service</h2>
              <p className="text-sm text-white/70 mt-0.5">Choose the type of service to add</p>
            </div>
          </div>
        </div>

        <div className="p-6 grid grid-cols-2 lg:grid-cols-3 gap-4">
          {SERVICE_TYPES_LIST.map(({ type, label, icon: Icon, gradient, desc }) => (
            <button
              key={type}
              onClick={() => onSelect(type)}
              className="group relative flex flex-col items-center gap-3 p-6 rounded-2xl border-2 border-gray-100 hover:border-purple-200 bg-white hover:shadow-xl transition-all duration-200 text-left overflow-hidden hover:-translate-y-1"
            >
              <div className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-0 group-hover:opacity-5 transition-opacity rounded-2xl`} />
              <div className={`p-3.5 rounded-xl bg-gradient-to-br ${gradient} shadow-md group-hover:scale-110 group-hover:shadow-lg transition-all duration-200`}>
                <Icon className="w-6 h-6 text-white" />
              </div>
              <div className="text-center">
                <p className="text-sm font-bold text-gray-800 group-hover:text-gray-900">{label}</p>
                <p className="text-[11px] text-gray-400 mt-0.5 leading-tight">{desc}</p>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─── Modal Router ─────────────────────────────────────────────────────────────
const ModalRouter = ({ serviceType, service, isOpen, onClose, onSave, inline }) => {
  const type = serviceType || service?.serviceType || 'venue';
  const commonProps = {
    key: service?._id || `new-${type}`,
    service,
    isOpen,
    onClose,
    onSave,
    inline,
  };
  switch (type) {
    case 'photography':  return <PhotographyModal {...commonProps} />;
    case 'decors':
    case 'decor':        return <DecorsModal {...commonProps} />;
    case 'caterers':
    case 'catering':     return <CaterersModal {...commonProps} />;
    case 'venue':
    default:             return <ServiceModal {...commonProps} />;
  }
};

// ─── AI Packages Preview (on the service card) ────────────────────────────────
const AIPackagesPreview = ({ service, onEdit }) => {
  const pkgs = service.aiPackages;
  if (!pkgs?.length) return null;

  return (
    <div className="rounded-xl border border-gray-200/70 bg-gray-50/60 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200/70">
        <div className="flex items-center gap-1.5">
          <Package className="w-3 h-3 text-gray-400" />
          <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
            Packages
          </span>
          <span className="text-[10px] font-semibold text-gray-500 bg-white border border-gray-200 rounded-full px-1.5 leading-4">
            {pkgs.length}
          </span>
        </div>
        <button
          onClick={() => onEdit(service)}
          className="text-[10px] font-semibold text-gray-600 hover:text-gray-900 inline-flex items-center gap-0.5"
        >
          <Edit3 className="w-2.5 h-2.5" /> Edit
        </button>
      </div>

      {/* Dense one-line-per-package list — shows ALL packages */}
      <div className="divide-y divide-gray-200/60">
        {pkgs.map((pkg, i) => (
          <div key={i} className="flex items-center gap-2 px-3 py-1.5">
            <span className="text-xs font-semibold text-gray-700 truncate flex-1 min-w-0">
              {pkg.name || `Package ${i + 1}`}
            </span>
            {pkg.tier && (
              <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wide flex-shrink-0 hidden sm:inline">
                {pkg.tier}
              </span>
            )}
            {pkg.price > 0 && (
              <span className="text-xs font-bold text-gray-900 whitespace-nowrap flex-shrink-0">
                ₹{Number(pkg.price).toLocaleString('en-IN')}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Pricing Display ──────────────────────────────────────────────────────────
const PricingSection = ({ service }) => {
  const { pricing } = service;
  if (!pricing) return null;

  if (Array.isArray(pricing)) {
    if (pricing.length === 0) return <p className="text-xs text-gray-400 italic">Pricing not set</p>;

    return (
      <div className="space-y-1.5">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Packages</p>
        <div className="flex flex-col gap-2">
          {pricing.slice(0, 3).map((pkg, i) => {
            const travel = getOutStationBadge(pkg.out_station);
            return (
              <div key={i} className="rounded-xl border border-gray-200/70 bg-gray-50/60 px-3 py-2 space-y-1.5">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-gray-700">{pkg.packageName || `Package ${i + 1}`}</p>
                  {pkg.price > 0 && (
                    <p className="text-sm font-bold text-gray-900">₹{pkg.price.toLocaleString('en-IN')}</p>
                  )}
                </div>
                {travel && (
                  <span className={`inline-flex items-center text-[10px] font-semibold border px-2 py-0.5 rounded-full ${travel.cls}`}>
                    {travel.label}
                  </span>
                )}
              </div>
            );
          })}
          {pricing.length > 3 && (
            <p className="text-xs text-gray-400 font-medium">+{pricing.length - 3} more packages</p>
          )}
        </div>
      </div>
    );
  }

  if (service.serviceType === 'caterers' || service.serviceType === 'catering') {
    const { model, perPlate, packagePerDay } = pricing;

    const showPerPlate  = (model === 'per_plate'       || model === 'mixed') && perPlate     && (perPlate.veg > 0     || perPlate.nonVeg > 0);
    const showPkgPerDay = (model === 'package_per_day' || model === 'mixed') && packagePerDay && (packagePerDay.veg > 0 || packagePerDay.nonVeg > 0);

    return (
      <div className="space-y-2">
        {showPerPlate && (
          <div className="rounded-xl border border-gray-200/70 bg-gray-50/60 p-3 space-y-1.5">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Price Per Plate</p>
            <div className="flex gap-2">
              {perPlate.veg > 0 && (
                <div className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-lg px-2.5 py-1.5 flex-1">
                  <span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />
                  <div>
                    <p className="text-[10px] text-gray-500 font-medium">Veg</p>
                    <p className="text-sm font-bold text-gray-900">₹{perPlate.veg.toLocaleString('en-IN')}</p>
                  </div>
                </div>
              )}
              {perPlate.nonVeg > 0 && (
                <div className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-lg px-2.5 py-1.5 flex-1">
                  <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" />
                  <div>
                    <p className="text-[10px] text-gray-500 font-medium">Non-Veg</p>
                    <p className="text-sm font-bold text-gray-900">₹{perPlate.nonVeg.toLocaleString('en-IN')}</p>
                  </div>
                </div>
              )}
            </div>
            {perPlate.minPax > 0 && (
              <p className="text-[10px] text-gray-400 flex items-center gap-1">
                <Users className="w-3 h-3" /> Min {perPlate.minPax} pax
              </p>
            )}
          </div>
        )}

        {showPkgPerDay && (
          <div className="rounded-xl border border-gray-200/70 bg-gray-50/60 p-3 space-y-1.5">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Package Starts from</p>
            <div className="flex gap-2">
              {packagePerDay.veg > 0 && (
                <div className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-lg px-2.5 py-1.5 flex-1">
                  <span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />
                  <div>
                    <p className="text-[10px] text-gray-500 font-medium">Veg</p>
                    <p className="text-sm font-bold text-gray-900">₹{packagePerDay.veg.toLocaleString('en-IN')}</p>
                  </div>
                </div>
              )}
              {packagePerDay.nonVeg > 0 && (
                <div className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-lg px-2.5 py-1.5 flex-1">
                  <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" />
                  <div>
                    <p className="text-[10px] text-gray-500 font-medium">Non-Veg</p>
                    <p className="text-sm font-bold text-gray-900">₹{packagePerDay.nonVeg.toLocaleString('en-IN')}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {!showPerPlate && !showPkgPerDay && (
          <p className="text-xs text-gray-400 italic">Pricing not set</p>
        )}
      </div>
    );
  }

  const { model, perPax, fixedBooking, packagePerDay } = pricing;
  const showPerPax  = perPax && (perPax.veg > 0 || perPax.nonVeg > 0);
  const showFixed   = fixedBooking?.basePrice > 0;
  const showPackage = packagePerDay?.price > 0;
  const isMixed     = model === 'mixed';

  return (
    <div className="space-y-2">
      {(model === 'per_pax' || isMixed) && showPerPax && (
        <div className="rounded-xl border border-gray-200/70 bg-gray-50/60 p-3 space-y-1.5">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Price Per Plate</p>
          <div className="flex gap-2">
            {perPax.veg > 0 && (
              <div className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-lg px-2.5 py-1.5 flex-1">
                <span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />
                <div>
                  <p className="text-[10px] text-gray-500 font-medium">Veg</p>
                  <p className="text-sm font-bold text-gray-900">₹{perPax.veg}</p>
                </div>
              </div>
            )}
            {perPax.nonVeg > 0 && (
              <div className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-lg px-2.5 py-1.5 flex-1">
                <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" />
                <div>
                  <p className="text-[10px] text-gray-500 font-medium">Non-Veg</p>
                  <p className="text-sm font-bold text-gray-900">₹{perPax.nonVeg}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {(model === 'fixed' || model === 'fixed_booking' || isMixed) && showFixed && (
        <div className="rounded-xl border border-gray-200/70 bg-gray-50/60 p-3">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-0.5">Fixed Booking</p>
          <p className="text-sm font-bold text-gray-900">₹{fixedBooking.basePrice.toLocaleString('en-IN')}</p>
        </div>
      )}
      {(model === 'package_per_day' || isMixed) && showPackage && (
        <div className="rounded-xl border border-gray-200/70 bg-gray-50/60 p-3">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-0.5">Package / Day</p>
          <p className="text-sm font-bold text-gray-900">₹{packagePerDay.price.toLocaleString('en-IN')}</p>
          <div className="flex gap-2 mt-1 flex-wrap">
            {packagePerDay.noOfPax > 0 && (
              <span className="text-[10px] bg-white text-gray-600 border border-gray-200 px-2 py-0.5 rounded-md inline-flex items-center gap-1"><Users className="w-3 h-3" /> {packagePerDay.noOfPax} pax</span>
            )}
            {packagePerDay.noOfRooms > 0 && (
              <span className="text-[10px] bg-white text-gray-600 border border-gray-200 px-2 py-0.5 rounded-md inline-flex items-center gap-1"><BedDouble className="w-3 h-3" /> {packagePerDay.noOfRooms} rooms</span>
            )}
          </div>
        </div>
      )}
      {!showPerPax && !showFixed && !showPackage && (
        <p className="text-xs text-gray-400 italic">Pricing not set</p>
      )}
    </div>
  );
};

// ─── Type-specific extra details ──────────────────────────────────────────────
const ExtraDetails = ({ service }) => {
  const type = service.serviceType;

  if (type === 'photography') {
    return service.typeOfServices?.length > 0 ? (
      <div className="space-y-1.5">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Services Offered</p>
        <div className="flex flex-wrap gap-1.5">
          {service.typeOfServices.slice(0, 4).map((s, i) => (
            <span key={i} className="text-xs bg-white text-gray-600 border border-gray-200 px-2 py-0.5 rounded-full capitalize">
              {s.replace(/_/g, ' ')}
            </span>
          ))}
          {service.typeOfServices.length > 4 && (
            <span className="text-xs bg-white text-gray-400 border border-gray-200 px-2 py-0.5 rounded-full">
              +{service.typeOfServices.length - 4}
            </span>
          )}
        </div>
      </div>
    ) : null;
  }

  if (type === 'caterers' || type === 'catering') {
    return (
      <div className="space-y-2">
        {service.foodType && (
          <div className="flex items-center gap-2 flex-wrap">
            {(service.foodType === 'veg' || service.foodType === 'both') && (
              <div className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-lg px-2.5 py-1.5">
                <span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />
                <span className="text-xs font-semibold text-gray-700">
                  {service.foodType === 'both' ? 'Veg' : 'Veg Only'}
                </span>
              </div>
            )}
            {(service.foodType === 'non-veg' || service.foodType === 'non_veg' || service.foodType === 'both') && (
              <div className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-lg px-2.5 py-1.5">
                <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" />
                <span className="text-xs font-semibold text-gray-700">
                  {service.foodType === 'both' ? 'Non-Veg' : 'Non-Veg Only'}
                </span>
              </div>
            )}
          </div>
        )}
        {service.typeOfCuisine?.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {service.typeOfCuisine.slice(0, 4).map((c, i) => (
              <span key={i} className="text-xs bg-white text-gray-600 border border-gray-200 px-2 py-0.5 rounded-full capitalize">
                {c.replace(/_/g, ' ')}
              </span>
            ))}
            {service.typeOfCuisine.length > 4 && (
              <span className="text-xs bg-white text-gray-400 border border-gray-200 px-2 py-0.5 rounded-full">
                +{service.typeOfCuisine.length - 4}
              </span>
            )}
          </div>
        )}
      </div>
    );
  }

  if (type === 'decors' || type === 'decor') {
    return (
      <div className="space-y-2">
        {service.drone_shoot_available && (
          <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2">
            <span className="text-xs font-semibold text-gray-700">Drone Shoot Available</span>
          </div>
        )}
        {service.subCategories?.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {service.subCategories.slice(0, 3).map((c, i) => (
              <span key={i} className="text-xs bg-white text-gray-600 border border-gray-200 px-2 py-0.5 rounded-full capitalize">
                {c.replace(/_/g, ' ')}
              </span>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (type === 'venue') {
    return service.subCategories?.length > 0 ? (
      <div className="space-y-1.5">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Venue Type</p>
        <div className="flex flex-wrap gap-1.5">
          {service.subCategories.slice(0, 3).map((c, i) => (
            <span key={i} className="text-xs bg-white text-gray-600 border border-gray-200 px-2 py-0.5 rounded-full capitalize">
              {c.replace(/_/g, ' ')}
            </span>
          ))}
          {service.subCategories.length > 3 && (
            <span className="text-xs bg-white text-gray-400 border border-gray-200 px-2 py-0.5 rounded-full">
              +{service.subCategories.length - 3}
            </span>
          )}
        </div>
      </div>
    ) : null;
  }

  return null;
};

// ─── Service Card ─────────────────────────────────────────────────────────────
const ServiceCard = ({ service, onEdit, onToggle, onDelete, loading, toggling }) => {
  const config = getConfig(service.serviceType);
  const Icon = config.icon;
  const hasImage = service.images?.length > 0;
  const photoCount = service.images?.length || 0;

  return (
    <div className="group relative bg-white rounded-3xl border border-gray-200/70 shadow-[0_1px_3px_rgba(16,24,40,0.04)] hover:shadow-xl hover:shadow-purple-900/5 hover:border-purple-200 hover:-translate-y-0.5 transition-all duration-300 flex flex-col">

      {/* Everything above the action bar greys out while the service is hidden */}
      <div className={`flex flex-col flex-grow transition-all duration-300 ${service.isLive ? '' : 'grayscale opacity-60'}`}>

      {/* Media — inset with its own rounded corners */}
      <div className="p-2.5 pb-0">
        <div className="relative h-44 overflow-hidden rounded-2xl bg-gray-100">
          {hasImage ? (
            <img
              src={service.images[0].url}
              alt={service.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling.style.display = 'flex'; }}
            />
          ) : null}
          <div
            className="flex-col items-center justify-center h-full bg-gradient-to-br from-purple-50 via-white to-pink-50"
            style={{ display: hasImage ? 'none' : 'flex' }}
          >
            <div className="p-3.5 rounded-2xl bg-gradient-to-br from-purple-600 to-pink-600 shadow-lg shadow-purple-600/20">
              <Icon className="w-7 h-7 text-white" />
            </div>
            <p className="mt-3 text-xs text-gray-500 font-medium">{hasImage ? 'Image unavailable' : 'No image yet'}</p>
          </div>
          {hasImage && <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-black/10 pointer-events-none" />}

          {/* Type */}
          <span className={`absolute top-2.5 left-2.5 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold shadow-sm backdrop-blur-sm ${config.badge}`}>
            <Icon className="w-3 h-3" />
            {config.label}
          </span>

          {/* Status */}
          <span className="absolute top-2.5 right-2.5 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/90 text-xs font-semibold text-gray-800 shadow-sm backdrop-blur-sm">
            <span className={`w-1.5 h-1.5 rounded-full ${service.isLive ? 'bg-green-500' : 'bg-gray-400'}`} />
            {service.isLive ? 'Live' : 'Inactive'}
          </span>

          {/* Photo count */}
          {hasImage && photoCount > 1 && (
            <span className="absolute bottom-2.5 left-2.5 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-black/50 text-[11px] font-semibold text-white backdrop-blur-sm">
              <Camera className="w-3 h-3" />
              {photoCount}
            </span>
          )}
        </div>
      </div>

      {/* Title row — logo overlaps the media edge */}
      <div className="relative px-5 pt-4">
        {service.logo && (
          <div className="absolute -top-7 right-5 w-12 h-12 rounded-2xl border-[3px] border-white shadow-md overflow-hidden bg-white">
            <img
              src={service.logo}
              alt="logo"
              className="w-full h-full object-cover"
              onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling.style.display = 'flex'; }}
            />
            <div className="hidden w-full h-full items-center justify-center bg-gray-100">
              <Icon className="w-5 h-5 text-gray-400" />
            </div>
          </div>
        )}
        <h3 className={`font-['Manrope'] text-lg font-extrabold tracking-tight text-gray-900 leading-tight line-clamp-1 ${service.logo ? 'pr-14' : ''}`}>
          {service.name || `${config.label} Service`}
        </h3>
        {service.serviceAddress && (
          <div className="flex items-center gap-1.5 mt-1.5 text-xs text-gray-500">
            <MapPin className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
            <span className="truncate">{service.serviceAddress.city}, {service.serviceAddress.state}</span>
          </div>
        )}
      </div>

      {/* Details */}
      <div className="flex flex-col flex-grow px-5 pt-3 pb-5 space-y-4">
        {service.description && (
          <p
            className="text-sm text-gray-500 line-clamp-2 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: service.description }}
          />
        )}

        <PricingSection service={service} />
        <AIPackagesPreview service={service} onEdit={onEdit} />
        <ExtraDetails service={service} />

        {service.paymentMethods?.length > 0 && (
          <div className="space-y-1.5">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Payment Method</p>
            <div className="flex gap-1.5 flex-wrap">
              {service.paymentMethods.map((m, i) => (
                <span key={i} className="text-xs bg-white text-gray-600 border border-gray-200 px-2 py-0.5 rounded-full capitalize">
                  {m}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      </div>

      {/* Actions — always full colour */}
      <div className="mt-auto flex items-center gap-3 rounded-b-3xl border-t border-gray-100 bg-gray-50/60 px-5 py-3.5">
        {/* Live switch */}
        <button
          type="button"
          role="switch"
          aria-checked={!!service.isLive}
          aria-label={service.isLive ? 'Deactivate service' : 'Activate service'}
          onClick={() => onToggle(service._id, service.serviceType, service.isLive)}
          disabled={loading || toggling}
          className="group/sw flex items-center gap-2.5 disabled:opacity-50"
        >
          <span className={`relative w-10 h-[22px] rounded-full transition-colors duration-200 ${service.isLive ? 'bg-green-500' : 'bg-red-500'}`}>
            <span className={`absolute top-0.5 left-0.5 w-[18px] h-[18px] rounded-full bg-white shadow transition-transform duration-200 ${service.isLive ? 'translate-x-[18px]' : 'translate-x-0'}`} />
          </span>
          <span className={`text-xs font-semibold ${service.isLive ? 'text-green-700' : 'text-red-600'}`}>{service.isLive ? 'Visible' : 'Hidden'}</span>
        </button>

        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={() => onDelete(service._id, service.serviceType)}
            disabled={loading}
            aria-label="Delete service"
            title="Delete service"
            className="flex h-9 w-9 items-center justify-center rounded-xl bg-white text-red-500 border border-red-200 hover:bg-red-50 active:scale-95 transition-all disabled:opacity-50"
          >
            <Trash2 className="w-4 h-4" />
          </button>
          <button
            onClick={() => onEdit(service)}
            className="inline-flex items-center justify-center gap-1.5 h-9 px-4 rounded-xl text-sm font-semibold active:scale-95 transition-all bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md shadow-purple-600/20 hover:shadow-lg"
          >
            <Edit3 className="w-3.5 h-3.5" />
            Edit
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Section Header ───────────────────────────────────────────────────────────
const SectionHeader = ({ type, count, onAdd }) => {
  const config = getConfig(type);
  const Icon = config.icon;
  return (
    <div className="flex items-center gap-3 mb-4">
      <div className="w-10 h-10 flex-shrink-0 rounded-xl bg-gradient-to-br from-purple-600 to-pink-600 shadow-md shadow-purple-600/20 flex items-center justify-center">
        <Icon className="w-5 h-5 text-white" />
      </div>
      <h3 className="font-['Manrope'] text-lg font-bold text-gray-900">{config.label}</h3>
      <span className="rounded-full border border-gray-200 bg-white px-2.5 py-0.5 text-xs font-semibold text-gray-600 tabular-nums">
        {count}
      </span>
      <div className="h-px flex-1 bg-gray-200" />
      {onAdd && (
        <button
          onClick={onAdd}
          className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-xl border border-gray-200 bg-white text-sm font-semibold text-gray-700 hover:border-purple-200 hover:bg-purple-50 transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span className="hidden sm:inline">Add {config.label}</span>
          <span className="sm:hidden">Add</span>
        </button>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const ServicesManagement = ({ onUpdate }) => {
  const [servicesByType, setServicesByType]       = useState({});
  const [loading, setLoading]                     = useState(false);
  const [canCreateServices, setCanCreateServices] = useState(false);
  const [vendorStatus, setVendorStatus]           = useState('');
  const [activeTab, setActiveTab]                 = useState('all');
  const [query, setQuery]                         = useState('');
  const [statusFilter, setStatusFilter]           = useState('all');
  const [togglingId, setTogglingId]               = useState(null);
  // Only the newest services response may update the list — older ones can arrive late
  const loadSeq = useRef(0);

  const [showPicker, setShowPicker]               = useState(false);
  const [modalOpen, setModalOpen]                 = useState(false);
  const [modalServiceType, setModalServiceType]   = useState(null);
  const [editingService, setEditingService]       = useState(null);
  const [inlineForm, setInlineForm]               = useState(false);

  useEffect(() => {
    loadServices();
    checkVendorStatus();
  }, []);

  const loadServices = async ({ silent = false } = {}) => {
    const seq = ++loadSeq.current;
    try {
      if (!silent) setLoading(true);
      const response = await api.get('/api/v1/services/');
      if (seq !== loadSeq.current) return; // a newer request has started — drop this stale result
      if (response.data.success) {
        const all      = response.data.data.services;
        // Keep every card in a fixed place (creation order), so a card never swaps
        // position with its neighbour when the list is reloaded after a change.
        const byCreation = (x, y) => String(x._id).localeCompare(String(y._id));
        const filtered = Object.fromEntries(
          Object.entries(all)
            .filter(([, arr]) => arr.length > 0)
            .map(([type, arr]) => [type, [...arr].sort(byCreation)])
        );
        setServicesByType(filtered);
      }
    } catch {
      if (seq === loadSeq.current) toast.error('Failed to load services');
    } finally {
      if (!silent && seq === loadSeq.current) setLoading(false);
    }
  };

  const checkVendorStatus = async () => {
    try {
      const response = await api.get('/vendors/profile');
      if (response.data.success) {
        const status = response.data.data.vendor.status;
        setCanCreateServices(status === 'approved');
        setVendorStatus(status);
      }
    } catch (error) {
      console.error(error);
    }
  };

  // Flip one service's live flag in local state, matched by id
  const setLocalLive = (serviceId, isLive) =>
    setServicesByType((prev) =>
      Object.fromEntries(
        Object.entries(prev).map(([type, arr]) => [
          type,
          arr.map((svc) => (svc._id === serviceId ? { ...svc, isLive } : svc)),
        ])
      )
    );

  const toggleServiceStatus = async (serviceId, serviceType, currentStatus) => {
    if (togglingId) return; // one change at a time
    const nextStatus = !currentStatus;
    setTogglingId(serviceId);
    setLocalLive(serviceId, nextStatus); // show the change on the clicked card straight away
    try {
      const response = await api.put(`/api/v1/services/${serviceId}`, {
        serviceType,
        isLive: nextStatus,
      });
      if (response.data.success) {
        toast.success(`Service ${nextStatus ? 'activated' : 'deactivated'}`);
        await loadServices({ silent: true });
        onUpdate?.();
      } else {
        setLocalLive(serviceId, currentStatus);
      }
    } catch (error) {
      setLocalLive(serviceId, currentStatus); // put the card back if the server refused
      toast.error(error.response?.data?.message || 'Failed to update service status');
    } finally {
      setTogglingId(null);
    }
  };

  const deleteService = async (serviceId, serviceType) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;
    try {
      setLoading(true);
      await api.delete(`/api/v1/services/${serviceId}?serviceType=${serviceType}`);
      toast.success('Service deleted successfully');
      loadServices();
      onUpdate?.();
    } catch {
      toast.error('Failed to delete service');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAdd = (type = null) => {
    setEditingService(null);
    if (type) {
      setModalServiceType(type);
      setInlineForm(true);
      setModalOpen(false);
    } else {
      setShowPicker(true);
    }
  };

  const handlePickType = (type) => {
    setShowPicker(false);
    setModalServiceType(type);
    setInlineForm(true);
    setModalOpen(false);
  };

  const handleEdit = (service) => {
    setEditingService(service);
    setModalServiceType(service.serviceType);
    setInlineForm(true);
    setModalOpen(false);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setInlineForm(false);
    setEditingService(null);
    setModalServiceType(null);
  };

  const handleSave = () => {
    handleCloseModal();
    loadServices();
    onUpdate?.();
  };

  const totalCount = Object.values(servicesByType).reduce((a, b) => a + b.length, 0);

  const allServices = Object.values(servicesByType).flat();
  const liveCount   = allServices.filter((svc) => svc.isLive).length;

  const matchesTab = (type, tab) =>
    tab === 'all' ||
    type === tab ||
    (tab === 'caterers' && type === 'catering') ||
    (tab === 'decors'   && type === 'decor');

  const tabCount = (tab) =>
    Object.entries(servicesByType).reduce((sum, [type, arr]) => sum + (matchesTab(type, tab) ? arr.length : 0), 0);

  // ── Filter services by tab, search text and live status ──
  const term = query.trim().toLowerCase();
  const filteredServices = Object.fromEntries(
    Object.entries(servicesByType)
      .filter(([type]) => matchesTab(type, activeTab))
      .map(([type, arr]) => [
        type,
        arr.filter((svc) => {
          if (statusFilter === 'live' && !svc.isLive) return false;
          if (statusFilter === 'inactive' && svc.isLive) return false;
          if (!term) return true;
          return [svc.name, svc.serviceAddress?.city, svc.serviceAddress?.state]
            .filter(Boolean)
            .some((v) => v.toLowerCase().includes(term));
        }),
      ])
      .filter(([, arr]) => arr.length > 0)
  );

  const filteredCount = Object.values(filteredServices).reduce((a, b) => a + b.length, 0);
  const isFiltering   = !!term || statusFilter !== 'all';

  if (loading && totalCount === 0) {
    return <SectionSkeleton cards={3} />;
  }

  const activeServiceType = editingService ? editingService.serviceType : modalServiceType;

  // ── Inline picker view ──
  if (showPicker) {
    return (
      <ServiceTypePicker
        isOpen={true}
        onSelect={handlePickType}
        onClose={() => setShowPicker(false)}
      />
    );
  }

  // ── Inline form view ──
  if (inlineForm && activeServiceType) {
    return (
      <div className="space-y-4">
        <button
          onClick={handleCloseModal}
          className="inline-flex items-center gap-1.5 h-9 pl-2.5 pr-3.5 rounded-xl border border-gray-200 bg-white text-sm font-semibold text-gray-700 hover:border-purple-200 hover:bg-purple-50 transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to Services
        </button>

        {/* No overflow clipping here — the form's header and footer are sticky */}
        <div className="bg-white rounded-2xl border border-gray-200/70 shadow-[0_1px_3px_rgba(16,24,40,0.04)]">
          <ModalRouter
            serviceType={activeServiceType}
            service={editingService}
            isOpen={true}
            onClose={handleCloseModal}
            onSave={handleSave}
            inline
          />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">

      {/* ── Banner ── */}
      <div className="relative overflow-hidden rounded-2xl border border-gray-200/70 bg-gradient-to-br from-purple-50 via-white to-pink-50 shadow-[0_1px_3px_rgba(16,24,40,0.04)] p-6 sm:p-7">
        <div className="pointer-events-none absolute -top-20 -right-16 h-56 w-56 rounded-full bg-purple-200/40 blur-3xl" />
        <div className="relative flex flex-col sm:flex-row sm:items-center gap-5">
          <div className="w-12 h-12 flex-shrink-0 rounded-2xl bg-gradient-to-br from-purple-600 to-pink-600 shadow-md shadow-purple-600/20 flex items-center justify-center">
            <Star className="w-6 h-6 text-white" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="font-['Manrope'] text-xl sm:text-2xl font-extrabold tracking-tight text-gray-900">My Services</h2>
            <p className="text-sm text-gray-600 mt-1">
              {totalCount > 0
                ? 'Everything customers can book from you, grouped by service type.'
                : 'Add your first service so customers can find and book you.'}
            </p>
          </div>
          {canCreateServices ? (
            <button
              onClick={() => handleOpenAdd()}
              className="inline-flex flex-shrink-0 items-center justify-center gap-2 h-11 px-5 rounded-xl text-sm font-semibold transition-all bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md shadow-purple-600/20 hover:shadow-lg"
            >
              <Plus className="w-4 h-4" />
              Add New Service
            </button>
          ) : (
            <div className="flex flex-shrink-0 items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
              <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
              <p className="text-sm text-amber-700">
                {vendorStatus === 'pending'
                  ? 'Your application is pending approval'
                  : 'Complete approval to add services'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ── Summary ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { icon: Package, label: 'Total services', value: totalCount },
          { icon: Eye,     label: 'Live',           value: liveCount },
          { icon: EyeOff,  label: 'Inactive',       value: totalCount - liveCount },
          { icon: Layers,  label: 'Service types',  value: Object.keys(servicesByType).length },
        ].map((stat) => (
          <div key={stat.label} className="bg-white rounded-2xl border border-gray-200/70 shadow-[0_1px_3px_rgba(16,24,40,0.04)] p-4 sm:p-5 flex items-center gap-4">
            <div className="w-11 h-11 flex-shrink-0 rounded-xl bg-purple-50 flex items-center justify-center">
              <stat.icon className="w-5 h-5 text-purple-600" />
            </div>
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 truncate">{stat.label}</p>
              <p className="font-['Manrope'] text-2xl font-extrabold tracking-tight text-gray-900 tabular-nums leading-tight">{stat.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* ── Toolbar: tabs + search + status ── */}
      <div className="bg-white rounded-2xl border border-gray-200/70 shadow-[0_1px_3px_rgba(16,24,40,0.04)] p-3 flex flex-col xl:flex-row xl:items-center gap-3">
        <div className="flex max-w-full items-center gap-1 overflow-x-auto scrollbar-none">
          {SERVICE_TABS.map(({ key, label, icon: Icon }) => {
            const isActive = activeTab === key;
            return (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-all duration-150
                  ${isActive
                    ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md shadow-purple-600/20'
                    : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
                  }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
                <span className={`rounded-full px-1.5 text-[11px] font-bold leading-5 tabular-nums ${isActive ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'}`}>
                  {tabCount(key)}
                </span>
              </button>
            );
          })}
        </div>

        <div className="flex flex-col sm:flex-row gap-3 xl:ml-auto">
          <div className="relative sm:w-64">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name or city"
              aria-label="Search services"
              className="w-full h-10 pl-10 pr-3 rounded-xl border border-gray-200 bg-white text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-4 focus:ring-purple-500/10 focus:border-purple-400 transition-all"
            />
          </div>
          <div className="inline-flex items-center gap-1 rounded-xl border border-gray-200 p-1 self-start">
            {[
              { key: 'all',      label: 'All' },
              { key: 'live',     label: 'Live' },
              { key: 'inactive', label: 'Inactive' },
            ].map((opt) => (
              <button
                key={opt.key}
                onClick={() => setStatusFilter(opt.key)}
                aria-pressed={statusFilter === opt.key}
                className={`px-3 h-8 rounded-lg text-xs font-semibold transition-colors ${
                  statusFilter === opt.key ? 'bg-purple-50 text-gray-900' : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Services grouped by type ── */}
      {filteredCount > 0 ? (
        <div className="space-y-8">
          {Object.entries(filteredServices).map(([type, services]) => (
            <section key={type}>
              <SectionHeader
                type={type}
                count={services.length}
                onAdd={canCreateServices ? () => handleOpenAdd(type) : undefined}
              />
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-5">
                {services.map((service) => (
                  <ServiceCard
                    key={service._id}
                    service={service}
                    loading={loading}
                    toggling={togglingId !== null}
                    onEdit={handleEdit}
                    onToggle={toggleServiceStatus}
                    onDelete={deleteService}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      ) : isFiltering && tabCount(activeTab) > 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-gray-300 px-6 py-14 text-center">
          <div className="w-14 h-14 bg-purple-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Search className="w-6 h-6 text-purple-500" />
          </div>
          <h3 className="font-['Manrope'] text-base font-bold text-gray-900">No services match your filters</h3>
          <p className="text-sm text-gray-500 mt-1 mb-5">Try a different search or status.</p>
          <button
            onClick={() => { setQuery(''); setStatusFilter('all'); }}
            className="inline-flex items-center h-10 px-4 rounded-xl border border-gray-200 bg-white text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Clear filters
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-dashed border-gray-300 px-6 py-16 text-center">
          <div className="w-16 h-16 bg-purple-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Star className="w-8 h-8 text-purple-400" />
          </div>
          <h3 className="font-['Manrope'] text-xl font-bold text-gray-900 mb-1">
            {activeTab === 'all' ? 'No Services Added' : `No ${getConfig(activeTab).label} Services`}
          </h3>
          <p className="text-sm text-gray-500 mb-6">
            {canCreateServices
              ? 'Start by adding your first service to attract customers'
              : 'Complete vendor approval to start adding services'}
          </p>
          {canCreateServices && (
            <button
              onClick={() => handleOpenAdd(activeTab === 'all' ? null : activeTab)}
              className="inline-flex items-center gap-2 h-11 px-6 rounded-xl font-semibold text-sm transition-all bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md shadow-purple-600/20 hover:shadow-lg"
            >
              <Plus className="w-4 h-4" />
              {activeTab === 'all' ? 'Add Your First Service' : `Add ${getConfig(activeTab).label}`}
            </button>
          )}
        </div>
      )}

      {/* ── Modal Router (fallback for non-inline usage) ── */}
      {modalOpen && (
        <ModalRouter
          serviceType={activeServiceType}
          service={editingService}
          isOpen={modalOpen}
          onClose={handleCloseModal}
          onSave={handleSave}
        />
      )}
    </div>
  );
};

export default ServicesManagement;